import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { FolderAndStandardByDepositId } from '@connect-services/client/components/project-settings/project-layers-settings/project-layers-settings.component';
import {
    DataEntity,
    Deposit,
    DepositControllerGetRequestParams,
    DepositsService,
    Layer,
    LayersService,
    LayerWithAttributes,
    SearchEntity,
} from '@connect-services/client/sdk/api';
import { defaultPaginationParameters, OrderEnum, paginationMaxTake } from '@shared/client/models/pagination.model';

type TableIconOptions = {
    color: string;
    icon: IconProp;
    text?: string;
};

@Injectable({
    providedIn: 'root',
})
export class DepositService {
    public readonly depositFolderId: WritableSignal<string | undefined> = signal(undefined);
    public readonly newDeposit: WritableSignal<Deposit | undefined> = signal(undefined);

    private readonly depositService = inject(DepositsService);
    private readonly sdkLayersService = inject(LayersService);
    private readonly translocoService = inject(TranslocoService);

    public async getAllDeposit(depositFolderId?: string, onlyValid: boolean = false): Promise<Deposit[]> {
        const paginationParams: DepositControllerGetRequestParams = {
            ...defaultPaginationParameters,
            depositFolder: depositFolderId,
            onlyValid: onlyValid,
            order: OrderEnum.DESC,
            orderBy: 'version',
            page: 0,
            take: paginationMaxTake,
        };
        let hasNextPage = false;

        const allDeposits: Deposit[] = [];

        do {
            const paginatedDeposits = await firstValueFrom(
                this.depositService.depositControllerGet({ ...paginationParams }),
            );

            allDeposits.push(...paginatedDeposits.items);
            paginationParams.page! += 1;
            hasNextPage = paginatedDeposits.hasNextPage;
        } while (hasNextPage);

        return allDeposits;
    }

    public getDepositEntityData(searchEntity: SearchEntity): Promise<DataEntity> {
        return firstValueFrom(
            this.sdkLayersService.layerControllerGetLayersEntity({
                entityId: searchEntity.id,
                id: searchEntity.data.parentId,
            }),
        );
    }

    public async getDepositsWithFolderAndStandardForLayers(
        layers: (Layer | LayerWithAttributes)[],
    ): Promise<FolderAndStandardByDepositId> {
        const deposits: FolderAndStandardByDepositId = new Map();

        const uniqueDepositIds = new Set<string>();

        for (const layer of layers) {
            if (layer?.deposit && !deposits.has(layer.deposit)) {
                uniqueDepositIds.add(layer.deposit);
            }
        }

        const depositRequests = Array.from(uniqueDepositIds).map((depositId) =>
            firstValueFrom(
                this.depositService.depositControllerGetDepositWithFolderAndStandard({ id: depositId }),
            ).then((value) => ({ depositId, value })),
        );

        const results = await Promise.allSettled(depositRequests);

        for (const result of results) {
            if (result.status === 'fulfilled') {
                deposits.set(result.value.depositId, result.value.value);
            }
        }

        return deposits;
    }

    public getIconOptions(status: Deposit.StatusEnum): TableIconOptions {
        switch (status) {
            case Deposit.StatusEnum.DONE:
                return {
                    color: 'text-green',
                    icon: 'circle',
                    text: this.translocoService.translate('success'),
                };
            case Deposit.StatusEnum.EXTRACTING:
                return {
                    color: 'text-blue',
                    icon: 'circle',
                    text: this.translocoService.translate('extracting'),
                };
            case Deposit.StatusEnum.FAILED:
                return {
                    color: 'text-red',
                    icon: 'circle',
                    text: this.translocoService.translate('fail', { COUNT: 1 }),
                };
            case Deposit.StatusEnum.IMPORTING:
                return {
                    color: 'text-orange',
                    icon: 'circle',
                    text: this.translocoService.translate('importing'),
                };
            default:
                throw new Error('Unexpected status');
        }
    }
}
